import cx from 'classnames';
import type PhotoSwipe from 'photoswipe';
import {useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import Draggable, {type DraggableEventHandler} from 'react-draggable';
import {AdConfigKey} from '~/ads/getAdConfig';
import {GoogleAdBanner} from '~/common/fabric/GoogleAds/GoogleAdBanner';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {ArrowIcon} from '~library/svg/ArrowIcon';

import {fog} from '~libSass/base/_exports.sass';
import './PhotoAdOverlay.sass';

declare global {
  interface Window {
    pswp: PhotoSwipe;
  }
}

const FIRST_INSERTION_POSITION =
  (evite.features.get('photo_ad_first_insertion_position') as number) || 2;
const INSERTION_INTERVAL = (evite.features.get('photo_ad_insertion_interval') as number) || 5;
const MAX_AD_COUNT = (evite.features.get('photo_ad_session_max_count') as number) || 10;
const AD_SESSION_DURATION_SECONDS =
  (evite.features.get('photo_ad_session_duration_sec') as number) || 7200;

const AD_SESSION_KEY = 'photo-ad-session';

const intervals: number[] = [];
for (let i = 0; i <= MAX_AD_COUNT; i++) {
  intervals.push(1 + FIRST_INSERTION_POSITION + i * INSERTION_INTERVAL);
}

function getAdSession() {
  let session = localStorage.getItem(AD_SESSION_KEY);
  if (!session) {
    session = JSON.stringify({start: Date.now(), count: 0});
    localStorage.setItem(AD_SESSION_KEY, session);
  }
  return JSON.parse(session);
}

function resetAdSession() {
  const session = getAdSession();
  session.start = Date.now();
  session.count = 0;
  localStorage.setItem(AD_SESSION_KEY, JSON.stringify(session));
}

function incrementAdSession() {
  const session = getAdSession();
  session.count += 1;
  localStorage.setItem(AD_SESSION_KEY, JSON.stringify(session));
}

function isAddSessionExpired() {
  const session = getAdSession();
  return Date.now() - session.start > AD_SESSION_DURATION_SECONDS * 1000;
}

function shouldShowAd(incrementCount = false) {
  if (isAddSessionExpired()) {
    resetAdSession();
  }
  if (incrementCount) {
    incrementAdSession();
  }
  const session = getAdSession();
  return session.count <= MAX_AD_COUNT;
}

interface PhotoAdOverlayProps {
  imageCount: number;
}

export const PhotoAdOverlay = ({imageCount = 0}: PhotoAdOverlayProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [showingAd, setShowingAd] = useState(false);
  const [position, setPosition] = useState({x: 0, y: 0});
  const [animateClassName, setAnimateClassName] = useState<
    'slide-left' | 'slide-right' | undefined
  >();

  useEffect(() => {
    if (imageCount !== 0) {
      const atInterval = intervals.includes(imageCount);
      const shouldShow = shouldShowAd(atInterval);
      setShowingAd(shouldShow && atInterval);
    }
  }, [imageCount]);

  const onDragStop: DraggableEventHandler = useCallback((_event, data) => {
    if (Math.abs(data.x) > window.innerWidth * 0.5) {
      setPosition({x: data.x, y: 0});
      setAnimateClassName(data.x > 0 ? 'slide-right' : 'slide-left');
    } else {
      setPosition({x: 0, y: 0});
    }
  }, []);

  const onAnimationEnd = useCallback(() => {
    setShowingAd(false);
    setAnimateClassName(undefined);
    setPosition({x: 0, y: 0});
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        setShowingAd(false);
      }
    };

    // if we are showing the ad, make the arrow keys close the ad
    if (showingAd) {
      window.addEventListener('keydown', handleKeyDown as EventListener);
    }
    // disable the arrow keys of the PhotoSwipe library instance
    if (window.pswp?.options) {
      window.pswp.options.arrowKeys = !showingAd;
    }

    return () => {
      if (window.pswp?.options) {
        window.pswp.options.arrowKeys = true;
      }
      window.removeEventListener('keydown', handleKeyDown as EventListener);
    };
  }, [showingAd]);

  return showingAd
    ? createPortal(
        <Draggable
          axis="x"
          handle=".photo-ad-overlay__container"
          onStop={onDragStop}
          nodeRef={ref}
          scale={0.5}
          position={position}
          cancel=".allow-clicking, .google-free-banner"
        >
          <div
            className={cx('photo-ad-overlay__container', animateClassName)}
            ref={ref}
            onAnimationEnd={onAnimationEnd}
          >
            <div>
              <GoogleAdBanner
                index={1}
                location="mid"
                configKey={AdConfigKey.PHOTO_SHARE}
                pageType={AdConfigKey.PHOTO_SHARE}
              />
            </div>
            <Button
              data-qa-id="continue-ad-overlay"
              variant="transparent"
              className="photo-ad-overlay__continue-button allow-clicking"
              ariaLabel="close advertisement"
              onClick={() => setShowingAd(false)}
            >
              <Typography variant="paragraph1" color="fog">
                Next photo
              </Typography>
              <ArrowIcon direction="right" color={fog} />
            </Button>
          </div>
        </Draggable>,
        document.body
      )
    : null;
};
